import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { tokenStore } from '@/lib/api/token-store';

import { userSchema } from './schemas';
import type {
  AuthSuccess,
  LoginPasswordRequest,
  NewPasswordForm,
  OtpDispatch,
  SendOtpRequest,
  User,
} from './types';

/**
 * Take the session the server just issued.
 *
 * The `refreshToken` is captured, not discarded. `POST /auth/tracking/refresh` takes it in the
 * **body** — it is not the HttpOnly cookie ADR-0005 assumed — so login is the only place the
 * client ever receives one. Dropping it here is what made every refresh answer
 * `400 VALIDATION_FAILED` and every session die at access-token expiry.
 *
 * It is optional on the type because the reset-password path adopts a session without one.
 */
function adoptSession(payload: {
  accessToken: string;
  refreshToken?: string;
  user: unknown;
}): AuthSuccess {
  const { accessToken, refreshToken, user } = payload;
  const parsed = userSchema.parse(user);
  tokenStore.setAccessToken(accessToken);
  if (refreshToken) tokenStore.setRefreshToken(refreshToken);
  return { accessToken, user: parsed };
}

export async function loginWithPassword(body: LoginPasswordRequest): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<{ accessToken: string; refreshToken?: string; user: unknown }>(
      endpoints.auth.login,
      body,
    ),
  );
}

export async function sendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpSend, body);
}

export async function resendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpResend, body);
}

export async function verifyLoginOtp(body: {
  referenceId: string;
  otp: string;
}): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<{ accessToken: string; refreshToken?: string; user: unknown }>(
      endpoints.auth.otpVerify,
      body,
    ),
  );
}

export async function sendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotSend, { phone });
}

export async function resendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotResend, { phone });
}

export async function verifyForgotOtp(body: { referenceId: string; otp: string }): Promise<string> {
  const { passwordResetToken } = await apiClient.post<{ passwordResetToken: string }>(
    endpoints.auth.forgotVerify,
    body,
  );
  tokenStore.setResetToken(passwordResetToken);
  return passwordResetToken;
}

/**
 * Sends **both** halves of the pair. The endpoint validates the match server-side and rejects a
 * body carrying `newPassword` alone with `400 VALIDATION_FAILED` / `confirmPassword: "Passwords do
 * not match"` — the client-side `newPasswordSchema` refine is a courtesy, not the authority. Same
 * payload Tracking Mobile sends. (Profile change-password is the opposite: `newPassword` alone.)
 */
export async function resetPassword(body: NewPasswordForm): Promise<void> {
  const token = tokenStore.getResetToken();
  await apiClient.post(endpoints.auth.forgotReset, body, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

export async function fetchCurrentUser(): Promise<User> {
  return userSchema.parse(await apiClient.get(endpoints.auth.me));
}

export async function logout(): Promise<void> {
  await apiClient.post(endpoints.auth.logout);
  tokenStore.clear();
}

/**
 * Restore the session on boot — the call `<AuthProvider>` makes before deciding `authenticated`
 * vs `unauthenticated`.
 *
 * Sends the stored refresh token and adopts the rotated pair, exactly as the interceptor's own
 * refresh does. It previously posted **no body**, which the API answers
 * `400 VALIDATION_FAILED { field: 'refreshToken' }`, so this always failed and every page load
 * landed on the login screen. Throwing when there is nothing to send keeps the boot honest: no
 * token means no session to restore, and spending a request to be told so is noise on an endpoint
 * that is rate limited.
 */
export async function refreshSession(): Promise<User> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const session = await apiClient.post<{ accessToken?: string; refreshToken?: string }>(
    endpoints.auth.refresh,
    { refreshToken },
  );
  if (!session?.accessToken) throw new Error('Refresh returned no access token');

  tokenStore.setAccessToken(session.accessToken);
  // Rotation is the server's choice; keep the token just sent if it answered without a new one.
  if (session.refreshToken) tokenStore.setRefreshToken(session.refreshToken);

  return fetchCurrentUser();
}
